import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import './App.css';

// Lazy load all pages for better performance
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectWorkspacePage = lazy(() => import('./pages/ProjectWorkspacePage'));
const OvertimeClaimsPage = lazy(() => import('./pages/OvertimeClaimsPage'));
const ReimbursementClaimsPage = lazy(() => import('./pages/ReimbursementClaimsPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const EditProjectPage = lazy(() => import('./pages/EditProjectPage'));
const KanbanBoardPage = lazy(() => import('./pages/KanbanBoardPage'));
const ZohoBooksPage = lazy(() => import('./pages/ZohoBooksPage'));
const ClaimActionPage = lazy(() => import('./pages/ClaimActionPage'));
const EmployeeDirectoryPage = lazy(() => import('./pages/EmployeeDirectoryPage'));
const DebriefPage = lazy(() => import('./pages/DebriefPage'));
const PitchInternalPage = lazy(() => import('./pages/PitchInternalPage'));
const DebriefAnalyticsPage = lazy(() => import('./pages/DebriefAnalyticsPage'));
const HolidaysPage = lazy(() => import('./pages/HolidaysPage'));
const FinanceReconciliationPage = lazy(() => import('./pages/FinanceReconciliationPage'));
const EstimatesPage = lazy(() => import('./pages/EstimatesPage'));
const EstimateDetailPage = lazy(() => import('./pages/EstimateDetailPage'));
const PricingSettingsPage = lazy(() => import('./pages/PricingSettingsPage'));

function PageLoader() {
  return (
    <div className="h-full flex items-center justify-center py-20">
      <div className="animate-spin w-6 h-6 border-2 border-foreground border-t-transparent rounded-full" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="h-screen bg-background flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-foreground border-t-transparent rounded-full" />
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/claims/action/:token" element={<ClaimActionPage />} />
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<DashboardPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="pitch-internal" element={<PitchInternalPage />} />
          <Route path="projects/:id" element={<ProjectWorkspacePage />} />
          <Route path="projects/:id/debrief" element={<DebriefPage />} />
          <Route path="projects/:id/edit" element={<EditProjectPage />} />
          <Route path="kanban" element={<KanbanBoardPage />} />
          <Route path="overtime-claims" element={<OvertimeClaimsPage />} />
          <Route path="reimbursement-claims" element={<ReimbursementClaimsPage />} />
          <Route path="directory" element={<EmployeeDirectoryPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="zoho-books" element={<ZohoBooksPage />} />
          <Route path="debrief-analytics" element={<DebriefAnalyticsPage />} />
          <Route path="holidays" element={<HolidaysPage />} />
          <Route path="finance/reconciliation" element={<FinanceReconciliationPage />} />
          <Route path="estimates" element={<EstimatesPage />} />
          <Route path="estimates/:id" element={<EstimateDetailPage />} />
          <Route path="pricing-settings" element={<PricingSettingsPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemedToaster />
          <RouteErrorBoundary routeKey="__app_root__">
            <AppRoutes />
          </RouteErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
